import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import moment, { Moment } from 'moment';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import { SpreadsheetEditor } from '@/components/views/Logistics';
import type {
    SpreadsheetData,
    DeliveryListFormData,
    DeliveryListPrintData,
    VehicleDropdownOption,
    MontageTeamDropdownOption,
} from '@/interfaces/logistics/LogisticsInterfaces';


export default /*#__PURE__*/_defineComponent({
  __name: 'DeliveryList',
  props: {
    printData: null,
    montageTeams: null,
    vehicles: null,
    totalPieces: null
  },
  emits: ["update:formField", "spreadsheet-change"],
  setup(__props: any, { expose, emit }: { emit: ({
    (
        e: 'update:formField',
        field: keyof DeliveryListFormData,
        value: DeliveryListFormData[keyof DeliveryListFormData],
    ): void;
    (e: 'spreadsheet-change', data: SpreadsheetData | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    printData: DeliveryListPrintData;
    montageTeams: MontageTeamDropdownOption[];
    vehicles: VehicleDropdownOption[];
    totalPieces: number;
};





expose({
    print,
});
const spreadsheetEditorRef = ref<InstanceType<typeof SpreadsheetEditor> | null>(null);

// Computed access to form data
const formData = computed(() => props.printData.formData);

// Datepicker locale
const locale = computed(() => getDatepickerLocale());

// Computed for delivery date as moment object
const deliveryDateMoment = computed({
    get: () => {
        if (!formData.value?.deliveryDate) return null;
        const parsed = moment(formData.value.deliveryDate, ['YYYY-MM-DD', 'DD.MM.YYYY', moment.ISO_8601]);
        return parsed.isValid() ? parsed : null;
    },
    set: (value: Moment | null) => {
        if (value && value.isValid()) {
            emit('update:formField', 'deliveryDate', value.format('DD.MM.YYYY'));
        } else {
            emit('update:formField', 'deliveryDate', '');
        }
    },
});

// Helper to get driver name from MontageTeam
function getDriverName(team: MontageTeamDropdownOption): string {
    return `${team.name || ''} ${team.lastName || ''}`.trim();
}

// Selected vehicle - maps to dropdown or keeps original string if no match
const selectedVehicle = computed(() => {
    const currentValue = formData.value?.vehicle;
    if (!currentValue) return undefined;
    const match = props.vehicles.find((v) => v.registration === currentValue);
    return match ? match.registration : currentValue;
});

// Selected driver - maps to dropdown or keeps original string if no match
const selectedDriver = computed(() => {
    const currentValue = formData.value?.driver;
    if (!currentValue) return undefined;
    const match = props.montageTeams.find((t) => getDriverName(t) === currentValue);
    return match ? getDriverName(match) : currentValue;
});

// Filter option for searchable selects
function filterOption(input: string, option: any): boolean {
    const label = option.componentOptions?.children?.[0]?.text || '';
    return label.toLowerCase().includes(input.toLowerCase());
}

// Handle vehicle selection change
function handleVehicleChange(value: string | undefined) {
    emit('update:formField', 'vehicle', value || '');
}

// Handle driver selection change
function handleDriverChange(value: string | undefined) {
    emit('update:formField', 'driver', value || '');
}

// Handle overall comment change
function handleOverallCommentChange(value: string) {
    emit('update:formField', 'overallComment', value || '');
}

// Convert spreadsheet data to table headers
const tableHeaders = computed(() => {
    const data = props.printData.spreadsheetData;
    if (!data?.rows?.[0]?.cells) return [];

    const headerRow = data.rows[0].cells;
    const headers: string[] = [];
    const cellKeys = Object.keys(headerRow)
        .map(Number)
        .sort((a, b) => a - b);

    for (const key of cellKeys) {
        headers.push(headerRow[key]?.text || '');
    }

    return headers;
});

// Convert spreadsheet data to table rows (excluding header)
const tableRows = computed(() => {
    const data = props.printData.spreadsheetData;
    if (!data?.rows) return [];

    const rows: string[][] = [];
    const rowKeys = Object.keys(data.rows)
        .map(Number)
        .sort((a, b) => a - b);
    const colCount = tableHeaders.value.length;

    // Skip row 0 (header)
    for (const rowKey of rowKeys) {
        if (rowKey === 0) continue;

        const rowData = data.rows[rowKey];
        if (!rowData?.cells) continue;

        const row: string[] = [];
        for (let col = 0; col < colCount; col++) {
            row.push(rowData.cells[col]?.text || '');
        }
        rows.push(row);
    }

    return rows;
});

function handleCellChange(change: { text: string; row: number; col: number }) {
    console.log('DeliveryList cell changed:', change);
}

function handleDataChange(data: SpreadsheetData | null) {
    emit('spreadsheet-change', data);
}

function print() {
    window.print();
}

return { __sfc: true,props, emit, spreadsheetEditorRef, formData, locale, deliveryDateMoment, getDriverName, selectedVehicle, selectedDriver, filterOption, handleVehicleChange, handleDriverChange, handleOverallCommentChange, tableHeaders, tableRows, handleCellChange, handleDataChange, print, SpreadsheetEditor }
}

})